import React, { useMemo } from 'react';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import { diffText, diffStats } from './diff';
import './DiffView.css';

/**
 * F12.4 — Side-by-side diff modal for two code snippets.
 *
 * Walks the diff op stream and renders one row per op. `eq` lines appear on
 * both sides; `del` lines on the left only; `add` lines on the right only.
 * Padding rows keep the columns aligned without needing fancy CSS grid math.
 *
 * Props:
 *  - isOpen, onClose       — passed to the underlying Modal
 *  - source: { lang, title?, text } — the "before" snippet (left column)
 *  - target: { lang, title?, text } — the "after"  snippet (right column)
 *
 * The two snippets are expected to share `lang` — the modal renders
 * regardless, but a `lang` mismatch warning is shown in the header.
 */
const DiffView = ({ isOpen, onClose, source, target }) => {
  const diff = useMemo(() => {
    if (!source || !target) return [];
    return diffText(source.text || '', target.text || '');
  }, [source, target]);

  const stats = useMemo(() => diffStats(diff), [diff]);
  const langMismatch =
    source && target && source.lang && target.lang && source.lang !== target.lang;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="card"
      size="lg"
      title="Compare snippets"
      ariaLabel="Diff view"
      className="diffview-modal"
    >
      <div className="diffview" data-testid="diff-view">
        <header className="diffview__head">
          <span className="diffview__source-name">
            {source?.title || `source.${source?.lang || 'txt'}`}
          </span>
          <span className="diffview__arrow" aria-hidden>→</span>
          <span className="diffview__target-name">
            {target?.title || `target.${target?.lang || 'txt'}`}
          </span>
          {langMismatch && (
            <span className="diffview__warn" role="note">
              <Icon name="alert" size={12} aria-hidden /> different languages
            </span>
          )}
        </header>

        <div
          className="diffview__rows"
          role="region"
          aria-label="Line-by-line diff"
        >
          {/* Both inputs empty → friendly placeholder. `diffText('','')` ends
              up with a single eq-empty row after newline-normalisation, so
              we detect emptiness at the source text level, not at diff.length. */}
          {(!source?.text && !target?.text) ? (
            <div className="diffview__empty" role="status">
              Both snippets are empty.
            </div>
          ) : (
            diff.map((line, i) => (
              <div
                key={i}
                className={`diffview__row diffview__row--${line.op}`}
                data-op={line.op}
              >
                <span className="diffview__gutter diffview__gutter--left" aria-hidden>
                  {line.op === 'add' ? '' : line.leftLine ?? ''}
                </span>
                <span className="diffview__cell diffview__cell--left">
                  <span className="diffview__sigil" aria-hidden>
                    {line.op === 'del' ? '−' : line.op === 'eq' ? ' ' : ''}
                  </span>
                  <span className="diffview__text">
                    {line.op === 'add' ? '' : line.text}
                  </span>
                </span>
                <span className="diffview__gutter diffview__gutter--right" aria-hidden>
                  {line.op === 'del' ? '' : line.rightLine ?? ''}
                </span>
                <span className="diffview__cell diffview__cell--right">
                  <span className="diffview__sigil" aria-hidden>
                    {line.op === 'add' ? '+' : line.op === 'eq' ? ' ' : ''}
                  </span>
                  <span className="diffview__text">
                    {line.op === 'del' ? '' : line.text}
                  </span>
                </span>
              </div>
            ))
          )}
        </div>

        <footer className="diffview__foot">
          <span className="diffview__stat diffview__stat--add">
            +{stats.added}
          </span>
          <span className="diffview__stat diffview__stat--del">
            −{stats.removed}
          </span>
          <span className="diffview__stat diffview__stat--eq">
            {stats.unchanged} unchanged
          </span>
        </footer>
      </div>
    </Modal>
  );
};

export default DiffView;
